<template>
    <div
        :key="index"
        class="p-4 rounded-lg flex flex-col gap-4 w-full"
        :class="[
            {
                'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700': backgroundColor === 'white',
                'bg-slate-50 dark:bg-slate-700/50': backgroundColor === 'slate',
            },
        ]"
    >
        <!-- Header with type and delete button -->
        <div class="flex justify-between items-center">
            <h3 class="text-base font-medium">
                {{
                    offerTypes.find((t) => t.value === offer.type)?.label ||
                    'Bonus Offer'
                }}
            </h3>
            <button
                type="button"
                class="btn-base btn-transparent !p-0"
                @click="removeBonusOffer"
            >
                <Icon icon="trash" class="w-4 h-4" />
                Delete
            </button>
        </div>

        <!-- Bonus inputs -->
        <div
            class="grid grid-cols-2 gap-y-6 gap-x-2"
            v-show="offer.type !== 'other'"
        >
            <!-- Bonus Amount -->
            <UiInput
                :id="`bonus-amount-${index}`"
                :name="`bonus-amount-${index}`"
                label="Bonus amount"
                :error="errors?.[`offer_${index}_bonus_amount`]"
                :options="
                    offer.bonus_units === 'dollars' ||
                    offer.bonus_units === 'statement_credits'
                        ? {
                              icon: 'dollar',
                              iconWrapperClass:
                                  'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                          }
                        : {}
                "
            >
                <template #input>
                    <div class="relative">
                        <VueNumberFormat
                            v-model:value="offer.bonus_amount"
                            aria-label="Bonus amount"
                            @focus="amountInputFocused = true"
                            @blur="amountInputFocused = false"
                            class="form-input w-full active border pr-9 validate disabled:cursor-not-allowed focus:z-10 active:z-10"
                            :class="
                                offer.bonus_units === 'dollars' ||
                                offer.bonus_units === 'statement_credits'
                                    ? '!pl-[54px]'
                                    : ''
                            "
                            :options="{
                                decimal: '.',
                                thousands: ',',
                                precision: 0,
                            }"
                        />
                        <label
                            class="left-3 top-1/2 -translate-y-1/2 text-slate-400 transition-all pointer-events-none bg-white dark:bg-slate-800"
                            :class="{
                                hidden:
                                    offer.bonus_amount || amountInputFocused,
                                absolute:
                                    !offer.bonus_amount && !amountInputFocused,
                                'pl-[54px]':
                                    offer.bonus_units === 'dollars' ||
                                    offer.bonus_units === 'statement_credits',
                            }"
                        >
                            Enter bonus amount
                        </label>
                    </div>
                </template>
            </UiInput>

            <!-- Bonus Units -->
            <UiInput
                :id="`bonus-units-${index}`"
                :name="`bonus-units-${index}`"
                label="Bonus type"
                :error="errors?.[`offer_${index}_bonus_units`]"
            >
                <template #input>
                    <ButtonPopover
                        :id="`bonus-units-popover-${index}`"
                        buttonClass="form-input w-full active border text-left flex items-center justify-between disabled:cursor-not-allowed"
                        placement="bottom-start"
                        :disabled="offer.received"
                    >
                        <template #button-content>
                            <span class="grow truncate">
                                {{
                                    getBonusUnitLabel(offer.bonus_units) ||
                                    'Bonus type'
                                }}
                            </span>
                            <Icon
                                icon="chevron-down"
                                class="w-4 h-4 ml-2 shrink-0"
                            />
                        </template>
                        <template #default="{ closePopover }">
                            <ul class="py-1 max-h-48 overflow-y-auto">
                                <li
                                    v-for="unit in bonusUnitOptions"
                                    :key="unit.value"
                                >
                                    <button
                                        type="button"
                                        class="w-full p-2 text-sm text-slate-900 dark:text-slate-100 rounded text-left hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none transition-colors"
                                        @click="
                                            selectBonusUnit(
                                                unit.value,
                                                closePopover
                                            )
                                        "
                                    >
                                        {{ unit.label }}
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>
                </template>
            </UiInput>

            <!-- Minimum Spend Amount -->
            <UiInput
                v-show="offer.type !== 'referral'"
                :id="`min-spend-${index}`"
                :name="`min-spend-${index}`"
                label="Minimum spend (if applicable)"
                :options="{
                    icon: 'dollar',
                    iconWrapperClass: 'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                }"
            >
                <template #input>
                    <VueNumberFormat
                        v-model:value="offer.minimum_spend_amount"
                        placeholder="Enter minimum spend"
                        class="form-input w-full active border pr-9 !pl-[54px] disabled:cursor-not-allowed"
                        min="0"
                        :options="{
                            decimal: '.',
                            thousands: ',',
                            precision: 0,
                        }"
                    />
                </template>
            </UiInput>
            <!-- Minimum Spend Deadline -->
            <UiInput
                v-show="offer.type !== 'referral'"
                :id="`spend-deadline-${index}`"
                :name="`spend-deadline-${index}`"
                label="Minimum spend deadline"
                :error="errors?.[`offer_${index}_minimum_spend_deadline`]"
                :disabled="offer.received"
                :options="{
                    icon: 'calendar',
                }"
            >
                <template #input>
                    <flat-pickr
                        :id="`spend-deadline-${index}`"
                        v-model="offer.minimum_spend_deadline"
                        type="date"
                        class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full validate disabled:cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                        placeholder="Minimum spend deadline"
                        :disabled="offer.received"
                        :config="minimumSpendDateConfig"
                    />
                </template>
            </UiInput>

            <!-- Date Bonus Received -->
            <div>
                <UiInput
                    :id="`date-received-${index}`"
                    :name="`date-received-${index}`"
                    label="Date bonus received"
                    :error="errors?.[`offer_${index}_date_received`]"
                    :disabled="!offer.received"
                    :options="{
                        icon: 'calendar',
                    }"
                >
                    <template #input>
                        <flat-pickr
                            :id="`date-received-${index}`"
                            v-model="offer.date_received"
                            type="date"
                            class="form-input date-picker flatpickr-input !pr-9 !pl-10 active border w-full validate disabled:cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                            :disabled="!offer.received"
                            :config="receivedDateConfig"
                        />
                    </template>
                </UiInput>

                <!-- Bonus received checkbox -->
                <div class="mt-2 flex items-center gap-2">
                    <input
                        type="checkbox"
                        :id="`bonus-received-${index}`"
                        v-model="offer.received"
                        class="form-checkbox"
                    />
                    <label :for="`bonus-received-${index}`" class="text-sm">
                        Bonus received
                    </label>
                </div>
            </div>
        </div>

        <!-- Dates row -->
        <div
            class="grid grid-cols-2 gap-4"
            v-show="offer.type !== 'other'"
        ></div>
        <div
            class="grid grid-cols-2 gap-4"
            v-show="offer.type === 'other' || offer.bonus_units === 'other'"
        >
            <UiInput
                label="Bonus details"
                :id="`bonus-details-${index}`"
                :name="`bonus-details-${index}`"
                class="col-span-2"
                :error="errors?.[`offer_${index}_details`]"
            >
                <template #input>
                    <textarea
                        type="textarea"
                        class="form-textarea w-full"
                        rows="5"
                        v-model="offer.details"
                    ></textarea>
                </template>
            </UiInput>
        </div>
        <Transition name="fade">
            <ButtonPopover
                v-show="isLastOffer"
                button-text="Add bonus offer"
                buttonClass="btn-base btn-transparent !pl-0"
                :icon="'plus'"
            >
                <template #default="{ closePopover }">
                    <ul>
                        <li
                            v-for="offerType in offerTypes"
                            :key="offerType.value"
                        >
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-slate-900 dark:text-slate-100 rounded text-left hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:outline-none transition-colors"
                                @click="
                                    selectOfferType(
                                        offerType.value,
                                        closePopover
                                    )
                                "
                            >
                                {{ offerType.label }}
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>
        </Transition>
    </div>
</template>

<script setup>
import { watch, ref } from 'vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';

const offer = defineModel('offer');
const amountInputFocused = ref(false);

const props = defineProps({
    index: {
        type: Number,
        required: true,
    },
    isLastOffer: {
        type: Boolean,
        default: false,
    },
    errors: Object,
    backgroundColor: { type: String, default: 'slate' },
});

const emit = defineEmits(['delete', 'add-offer']);

watch(
    () => offer.value.bonus_units,
    (newUnit, oldUnit) => {
        if (oldUnit === 'other' && newUnit !== 'other') {
            offer.value.details = null;
        }
    }
);

watch(
    () => offer.value.received,
    (currentValue, previousValue) => {
        if (previousValue && !currentValue) {
            offer.value.date_received = null;
        }
    }
);

const bonusUnitOptions = [
    { label: 'Points', value: 'points' },
    { label: 'Miles', value: 'miles' },
    { label: 'Cash back', value: 'dollars' },
    { label: 'Statement credits', value: 'statement_credits' },
    { label: 'Other', value: 'other' },
];

const offerTypes = [
    { label: 'Upgrade offer', value: 'upgrade' },
    { label: 'Retention offer', value: 'retention' },
    { label: 'Referral bonus', value: 'referral' },
    { label: 'Other', value: 'other' },
];

const receivedDateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
};
const minimumSpendDateConfig = {
    dateFormat: 'm/d/Y',
};

const getBonusUnitLabel = (value) => {
    const unit = bonusUnitOptions.find((u) => u.value === value);
    return unit ? unit.label : null;
};

const selectBonusUnit = (unitValue, closePopover) => {
    offer.value.bonus_units = unitValue;
    closePopover();
};

const removeBonusOffer = () => {
    emit('delete');
};

const selectOfferType = (type, closePopover) => {
    emit('add-offer', type);
    closePopover();
};
</script>
